import { denormalize, normalize, Schema } from 'normalizr';
import { createCachedSelector, FifoMapCache, LruMapCache, ParametricSelector } from 're-reselect';

import { emptyObject } from 'Lib/constants/placeholder';
import type { GlobalStateType } from 'Lib/reduxType';
import { ArraySchema } from 'Lib/schemas';

import { camelizeKeys } from './utils/createKeyProcessor';

export const CACHE_SIZE_ARRAY = 4;
export const CACHE_SIZE_DEFAULT = 10;
export const CACHE_STRATEGY_FIFO = 'fifo';
export const CACHE_STRATEGY_LRU = 'lru';

const CACHE_MAPPING = {
  [CACHE_STRATEGY_FIFO]: FifoMapCache,
  [CACHE_STRATEGY_LRU]: LruMapCache,
};
/**
 * Creates a selector to get a slice of state regardless of whether or not it is immutable
 *
 * This is intended primarily for use with root selectors, since the entire state isn't immutable.
 *
 * @param  {string} key - key to select from the state
 * @param  {*} [fallback] - default value of the key
 * @return {function} selector that grabs the key from the state
 */
export const createKeySelector =
  <T extends keyof GlobalStateType>(key: T) =>
  (state: GlobalStateType): GlobalStateType[T] =>
    // TODO: remove fallback as this lies to TS (createKeySelector should be able to return undefined)
    state[key] || emptyObject;

/**
 * Creates a selector that denormalizes according to a schema
 *
 * @param  {function} resultSelector - selector that returns the result to denormalize
 * @param  {function} entitiesSelector - selector that returns the entities to use for denormalization
 * @param  {Schema} schema - normalization schema
 * @return {function}
 */
export const createDenormalizedSelector = (
  resultSelector: ParametricSelector<any, any, any>,
  entitiesSelector: ParametricSelector<any, any, any>,
  schema: Schema,
  cacheSize: number = CACHE_SIZE_DEFAULT,
  cacheStragegy: string = CACHE_STRATEGY_FIFO
) =>
  createCachedSelector(resultSelector, entitiesSelector, (id, entities) =>
    denormalize(id, schema, entities)
  )({
    cacheObject: new (CACHE_MAPPING[cacheStragegy] || FifoMapCache)({ cacheSize }),
    keySelector: (_, key) => {
      if (schema instanceof ArraySchema) {
        if (key?.length) {
          return key.join(',');
        }
        return 'default-array-cache-key';
      }
      return key || 'default-entities-cache-key';
    },
  });

/**
 * Wraps a mapper so that it filters out falsey values
 *
 * TODO: REMOVE-TYPE-ASSERTION - fixing return type will allow us to remove several type assertions and annotations.
 *
 * @param  {function} mapper - function that maps an array or List
 * @return {function} wrapped function with a filter
 */
export const filteredMapper =
  (mapper) =>
  (...args: any[]) =>
    mapper(...args).filter(Boolean);

export type PayloadCreator<A extends any[], P> = (...args: A) => P;

type ActionReturnType<
  T extends string,
  PC extends PayloadCreator<any, any> | undefined
> = PC extends PayloadCreator<infer _A, infer _P>
  ? (...args: _A) => { type: T; payload: _P }
  : () => { type: T };
/**
 * Creates an action creator function
 *
 * @param  {string} type - action type
 * @param  {function} payloadCreator - function that creates the action payload
 * @return {function} an action creator
 */
export const createAction = <
  T extends string,
  A extends any[],
  P,
  PC extends PayloadCreator<A, P> | undefined = undefined
>(
  type: T,
  payloadCreator?: PC
): ActionReturnType<T, PC> => {
  if (payloadCreator === undefined) {
    return (() => ({ type })) as ActionReturnType<T, PC>;
  }

  return ((..._args: A) => ({
    type,
    payload: payloadCreator(..._args),
  })) as ActionReturnType<T, PC>;
};

/**
 * Performs out "standard" response processing, camelizing keys and normalizing according
 * to the supplied response schema.
 *
 * @param  {Response|Object} response - response from the API
 * @param  {Schema} [schema] - normalization schema
 * @param  {Schema} [processKeys] - customize or disable key processing. Converts all keys to camelCase by default.
 * @return {Object} normalized response
 */
export async function processResponse(
  // TODO: use proper response object type when ApiClient is migrated to TS
  response: { json: () => Promise<any> } | any,
  schema?: Schema | null,
  processKeys: Function | boolean = camelizeKeys
) {
  let data;
  // Parse JSON response, if necessary
  if (response && response.json) {
    try {
      data = await response.json();
    } catch (e) {
      data = {};
    }
  } else {
    data = response;
  }

  // Process keys (defaults to camelizing all keys)
  if (typeof processKeys === 'function') {
    data = processKeys(data);
  }

  // Normalize using the supplied schema
  if (schema && data) {
    data = normalize(data, schema);
  }

  return data;
}
